/**
 * Pet-owner pet shapes. Mirrors backend/src/modules/pet/pet.validators.ts.
 */

export type PetSpecies = 'DOG' | 'CAT' | 'BIRD' | 'RABBIT' | 'FISH' | 'OTHER';
export type PetSex = 'MALE' | 'FEMALE' | 'UNKNOWN';
export type NeuterStatus = 'NEUTERED' | 'NOT_NEUTERED' | 'UNKNOWN';

export type Pet = {
  id: string;
  owner_id: string;
  name: string;
  species: PetSpecies;
  breed_id: string | null;
  mixed_breed_description: string | null;
  birth_date: string | null; // YYYY-MM-DD
  approximate_age_months: number | null;
  sex: PetSex;
  neuter_status: NeuterStatus;
  weight_kg: number | null;
  color_markings: string | null;
  microchip_id: string | null;
  primary_photo_url: string | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type PetPhoto = {
  id: string;
  pet_id: string;
  is_primary: boolean;
  /** Relative API path; render via AuthImage, append `?variant=thumb|full`. */
  file_path: string;
  uploaded_at: string;
};

export type PetWriteRequest = {
  name: string;
  species: PetSpecies;
  breed_id?: string;
  mixed_breed_description?: string;
  birth_date?: string;
  approximate_age_months?: number;
  sex?: PetSex;
  neuter_status?: NeuterStatus;
  weight_kg?: number;
  color_markings?: string;
  microchip_id?: string;
  notes?: string;
};

export type Breed = {
  id: string;
  species: PetSpecies;
  name: string;
  size_category: string;
};

export const SPECIES_OPTIONS: Array<{ value: PetSpecies; label: string }> = [
  { value: 'DOG', label: 'Dog' },
  { value: 'CAT', label: 'Cat' },
  { value: 'BIRD', label: 'Bird' },
  { value: 'RABBIT', label: 'Rabbit' },
  { value: 'FISH', label: 'Fish' },
  { value: 'OTHER', label: 'Other' },
];

export const SEX_OPTIONS: Array<{ value: PetSex; label: string }> = [
  { value: 'UNKNOWN', label: 'Unknown' },
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
];

export const NEUTER_OPTIONS: Array<{ value: NeuterStatus; label: string }> = [
  { value: 'UNKNOWN', label: 'Unknown' },
  { value: 'NEUTERED', label: 'Neutered / Spayed' },
  { value: 'NOT_NEUTERED', label: 'Not neutered' },
];

// ---- Medical records ----

export type VaccinationType =
  | 'RABIES'
  | 'DHPP'
  | 'BORDETELLA'
  | 'LEPTOSPIROSIS'
  | 'CANINE_INFLUENZA'
  | 'LYME'
  | 'FVRCP'
  | 'FELV'
  | 'OTHER';

export type AllergySeverity = 'MILD' | 'MODERATE' | 'SEVERE';
export type ConditionStatus = 'ACTIVE' | 'RESOLVED' | 'CHRONIC';

export type Vaccination = {
  id: string;
  pet_id: string;
  type: VaccinationType;
  administered_on: string;
  next_due_on: string | null;
  administered_by: string | null;
  created_at: string;
};

export type Allergy = {
  id: string;
  pet_id: string;
  allergen: string;
  severity: AllergySeverity;
  created_at: string;
};

export type Condition = {
  id: string;
  pet_id: string;
  condition: string;
  status: ConditionStatus;
  diagnosed_on: string | null;
  created_at: string;
};

export type CreateVaccinationRequest = {
  type: VaccinationType;
  administered_on: string;
  next_due_on?: string;
  administered_by?: string;
};
export type CreateAllergyRequest = { allergen: string; severity: AllergySeverity };
export type CreateConditionRequest = {
  condition: string;
  status: ConditionStatus;
  diagnosed_on?: string;
};

export const VACCINATION_TYPE_OPTIONS: Array<{ value: VaccinationType; label: string }> = [
  { value: 'RABIES', label: 'Rabies' },
  { value: 'DHPP', label: 'DHPP' },
  { value: 'BORDETELLA', label: 'Bordetella' },
  { value: 'LEPTOSPIROSIS', label: 'Leptospirosis' },
  { value: 'CANINE_INFLUENZA', label: 'Canine influenza' },
  { value: 'LYME', label: 'Lyme' },
  { value: 'FVRCP', label: 'FVRCP' },
  { value: 'FELV', label: 'FeLV' },
  { value: 'OTHER', label: 'Other' },
];

export const ALLERGY_SEVERITY_OPTIONS: Array<{ value: AllergySeverity; label: string }> = [
  { value: 'MILD', label: 'Mild' },
  { value: 'MODERATE', label: 'Moderate' },
  { value: 'SEVERE', label: 'Severe' },
];

export const CONDITION_STATUS_OPTIONS: Array<{ value: ConditionStatus; label: string }> = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'CHRONIC', label: 'Chronic' },
  { value: 'RESOLVED', label: 'Resolved' },
];
